import React, { forwardRef, useEffect, useImperativeHandle, useState, useContext } from 'react';
import {
  Card, Form, Row, Col, Select, Input, Button, DatePicker, InputNumber, Space, Typography, Divider,
  message,
} from 'antd';
import { SearchOutlined, ClearOutlined, UpOutlined, DownOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';

const { RangePicker } = DatePicker;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  label: `${String(i).padStart(2, '0')}:xx  (${i === 0 ? 'midnight' : i === 12 ? 'noon' : i < 12 ? `${i} AM` : `${i - 12} PM`})`,
  value: i,
}));

const DAY_OPTIONS = [
  { label: 'Sunday',    value: 1 },
  { label: 'Monday',    value: 2 },
  { label: 'Tuesday',   value: 3 },
  { label: 'Wednesday', value: 4 },
  { label: 'Thursday',  value: 5 },
  { label: 'Friday',    value: 6 },
  { label: 'Saturday',  value: 7 },
];

const YES_NO = [{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }];
const FLAGGED_OPTIONS = [
  { label: 'My flagged calls', value: 'mine' },
  { label: 'Any flagged calls', value: 'any' },
];
const IS_RESOLVED = [{ label: 'Yes', value: 'TRUE' }, { label: 'No', value: 'FALSE' }];

function SectionHeader({ label, collapsed, onToggle }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 4,
      padding: '5px 12px', marginBottom: collapsed ? 12 : 10,
    }}>
      <Typography.Text strong style={{ fontSize: 13 }}>{label}</Typography.Text>
      {/* No toggle: a label only (Call Filters on its own -- the panel's Hide folds it). */}
      {onToggle && (
        <Button size="small" type="text" icon={collapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={onToggle}>
          {collapsed ? 'Show' : 'Hide'}
        </Button>
      )}
    </div>
  );
}


const _FILTER_OPTS_TTL = 24 * 60 * 60 * 1000; // 24 hours — call log imports once per morning

function _cacheKey(userId) { return `qa_filter_options_${userId}`; }

function _getCachedOptions(userId) {
  try {
    const raw = sessionStorage.getItem(_cacheKey(userId));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    return (Date.now() - ts < _FILTER_OPTS_TTL) ? data : null;
  } catch { return null; }
}

function _setCachedOptions(userId, data) {
  try {
    sessionStorage.setItem(_cacheKey(userId), JSON.stringify({ data, ts: Date.now() }));
  } catch { /* sessionStorage full — skip caching */ }
}

function _saveSession(key, formValues) {
  if (!key) return;
  try {
    const v = { ...formValues };
    if (v.dateRange) {
      v.dateRange = Array.isArray(v.dateRange)
        ? v.dateRange.map((d) => (d ? d.format('YYYY-MM-DD') : null))
        : v.dateRange.format('YYYY-MM-DD');
    }
    sessionStorage.setItem(`fp_${key}`, JSON.stringify(v));
  } catch {}
}

function _loadSession(key) {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(`fp_${key}`);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (v.dateRange) {
      v.dateRange = Array.isArray(v.dateRange)
        ? v.dateRange.map((d, i) => d ? dayjs(d) : (i === 1 ? dayjs() : null))
        : dayjs(v.dateRange);
    }
    return v;
  } catch { return null; }
}

const FilterPanel = forwardRef(function FilterPanel({ onSearch, loading, buttonLabel = 'Search', hideAIFilters = false, hiddenFilters = null, optionsEndpoint = '/api/filter-options', defaultDateRange = null, requireCampaign = false, defaultFilters = null, showCalledFilters = false, sessionKey = null, singleDate = false, singleCampaign = false, autoSelectOnlyCampaign = true, noPastDate = false, searchNote = null }, ref) {
  const currentUser = useContext(UserContext);
  const analyticsTabs = currentUser?.AnalyticsTabs || [];
  const hasTab = (...keys) => analyticsTabs.length === 0 || keys.some((k) => analyticsTabs.includes(k));
  const hide = (key) => hiddenFilters?.has(key);

  const [form] = Form.useForm();

  useImperativeHandle(ref, () => ({
    setDateRange(range) { form.setFieldsValue({ dateRange: range }); },
    triggerSearch()     { handleSearch(); },
  }));

  // Call Filters fold on their own only beside the AI Filters (two sections);
  // alone, the panel's own Hide does it.
  const [callCollapsed, setCallCollapsed] = useState(false);
  // The whole panel folded to its header (the form stays mounted, so its
  // values are kept). Remembered per page for the browser session.
  const panelKey = `fp_${sessionKey || 'default'}_folded`;
  const [panelFolded, setPanelFolded] = useState(() => {
    try { return sessionStorage.getItem(panelKey) === '1'; } catch { return false; }
  });
  const foldPanel = (v) => {
    setPanelFolded(v);
    try { sessionStorage.setItem(panelKey, v ? '1' : '0'); } catch { /* private window */ }
  };
  const [aiCollapsed, setAiCollapsed]     = useState(true);
  const [optionsLoading, setOptionsLoading]   = useState(false);
  // Skill groups (Admin > Skill Groups): one picks its campaign's skills.
  // Either Skill Group or Skill is used, not both.
  const [skillGroups, setSkillGroups] = useState([]);
  useEffect(() => {
    api.get('/api/skill-groups').then((r) => setSkillGroups(r.data || [])).catch(() => setSkillGroups([]));
  }, []);
  const pickedCampaigns = Form.useWatch('campaign', form);
  const pickedGroup     = Form.useWatch('skill_group', form);
  const pickedSkills    = Form.useWatch('skill', form);
  const groupOptions = (() => {
    const camps = [].concat(pickedCampaigns ?? []).filter((x) => x !== undefined && x !== null && x !== '').map(String);
    return skillGroups
      .filter((sg) => !camps.length || camps.includes(String(sg.CXCampaignNo)))
      .map((sg) => ({ value: sg.ID, label: camps.length === 1 ? sg.Name : `${sg.Name} (${sg.CampaignName})` }));
  })();
  const [options, setOptions] = useState({
    campaigns: [], skills: [], agents: [], dispositions: [],
    reasons: [], outcomes: [], topic_categories: [], topics: [],
    sentiment_starts: [], sentiment_ends: [],
    media_names: [], directions: [], contact_types: [], contact_end_reasons: [],
    save_attempt_outcomes: [], upsell_circumstances: [], ai_flags: {},
    oppt_types: [], oppt_opportunities: [], oppt_outcomes: [], oppt_reasons: [],
    states: [], zips: [], stars: [],
  });

  useEffect(() => {
    if (!currentUser?.ID) return;
    const saved = _loadSession(sessionKey);
    // noPastDate (a single-date "today" page): a remembered date from an
    // earlier day opens as today instead.
    if (noPastDate && saved?.dateRange && !Array.isArray(saved.dateRange)
        && saved.dateRange.isBefore(dayjs(), 'day')) {
      saved.dateRange = dayjs();
    }
    form.setFieldsValue({
      dateRange: defaultDateRange || (singleDate ? dayjs() : [dayjs().subtract(14, 'day'), dayjs()]),
      direction: ['Inbound'],
      contact_type: ['Original'],
      ...(defaultFilters || {}),
      ...(saved || {}),
    });
    // Custom endpoints bypass the shared cache (different data source)
    if (optionsEndpoint !== '/api/filter-options') {
      let cancelled = false;
      const fetchWithRetry = (attempt) => {
        if (cancelled) return;
        setOptionsLoading(true);
        api.get(optionsEndpoint)
          .then((res) => {
            if (!cancelled) {
              setOptions((prev) => ({ ...prev, ...res.data }));
              setOptionsLoading(false);
            }
          })
          .catch((err) => {
            if (cancelled) return;
            if (attempt < 3) {
              setTimeout(() => fetchWithRetry(attempt + 1), 1500 * attempt);
            } else {
              setOptionsLoading(false);
              console.error('Failed to load filter options from', optionsEndpoint, err);
              message.error('Could not load filter options — please refresh the page.');
            }
          });
      };
      fetchWithRetry(1);
      return () => { cancelled = true; };
    }
    const cached = _getCachedOptions(currentUser.ID);
    if (cached) {
      setOptions(cached);
      return;
    }
    let cancelled = false;
    // The campaign list on its own first: it is quick, and the full options
    // below wait on every other list, which kept the Campaign filter empty.
    api.get('/api/filter-options/campaigns')
      .then((res) => {
        if (!cancelled) setOptions((prev) => (prev.campaigns.length ? prev : { ...prev, campaigns: res.data || [] }));
      })
      .catch(() => {});
    const fetchWithRetry = (attempt) => {
      if (cancelled) return;
      setOptionsLoading(true);
      api.get('/api/filter-options')
        .then((res) => {
          if (!cancelled) {
            setOptions(res.data);
            _setCachedOptions(currentUser.ID, res.data);
            setOptionsLoading(false);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          if (attempt < 3) {
            setTimeout(() => fetchWithRetry(attempt + 1), 1500 * attempt);
          } else {
            setOptionsLoading(false);
            console.error('Failed to load filter options', err);
            message.error('Could not load filter options — please refresh the page.');
          }
        });
    };
    fetchWithRetry(1);
    return () => { cancelled = true; };
  }, [form, currentUser?.ID, optionsEndpoint]); // eslint-disable-line

  // Someone who can see only one campaign gets it picked, unless a campaign
  // is already set (e.g. remembered from the last search).
  useEffect(() => {
    if (!autoSelectOnlyCampaign || options.campaigns.length !== 1) return;
    const cur = form.getFieldValue('campaign');
    if (cur != null && cur !== '' && !(Array.isArray(cur) && !cur.length)) return;
    const only = options.campaigns[0].value;
    form.setFieldValue('campaign', singleCampaign ? only : [only]);
  }, [options.campaigns]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    // getFieldsValue(true) reads the whole store, not just the fields mounted
    // right now. Collapsing a section unmounts its Form.Items, and without the
    // argument every value inside it comes back undefined -- so collapsing
    // Call Filters made a chosen campaign and date range look unset, and any
    // AI filter set before collapsing that section was dropped in silence.
    const v = form.getFieldsValue(true);

    // Normalize campaign for singleCampaign mode (session storage may have stored an old array)
    const campaignVal = singleCampaign
      ? (Array.isArray(v.campaign) ? v.campaign[0] ?? null : v.campaign ?? null)
      : v.campaign;

    if (requireCampaign && (singleCampaign ? !campaignVal : !campaignVal?.length)) {
      message.warning('Please select a Campaign before searching.');
      foldPanel(false);
      return;
    }

    if (!singleDate && (!v.dateRange?.[0] || !v.dateRange?.[1])) {
      message.warning('Please select a complete date range before searching.');
      foldPanel(false);
      return;
    }

    const filters = {};

    if (singleDate) {
      if (v.dateRange) { const ds = v.dateRange.format('YYYY-MM-DD'); filters.start_date = ds; filters.end_date = ds; }
    } else {
      if (v.dateRange?.[0]) filters.start_date = v.dateRange[0].format('YYYY-MM-DD');
      if (v.dateRange?.[1]) filters.end_date   = v.dateRange[1].format('YYYY-MM-DD');
    }
    if (v.contact_id)         filters.contact_id        = v.contact_id;
    if (v.master_contact_id)  filters.master_contact_id = v.master_contact_id;

    // Single campaign mode — pass scalar directly
    if (singleCampaign && campaignVal) filters.campaign = campaignVal;

    // Multi-select fields — join array to comma-separated string for the API
    const multi = [
      ...(singleCampaign ? [] : ['campaign']),
      'skill', 'agent_name', 'disp_name', 'media_name', 'direction',
      'contact_type', 'contact_end_reason', 'reason', 'outcome', 'topic_category',
      'sentiment_start', 'sentiment_end', 'save_attempt_outcome',
      'upsell_circumstance', 'oppt_type', 'oppt_opportunity', 'oppt_outcome',
      'oppt_reason', 'state', 'zip',
    ];
    multi.forEach((f) => { if (v[f]?.length) filters[f] = v[f].join(','); });
    // A Skill Group stands for its skills.
    if (v.skill_group) {
      const sg = skillGroups.find((x) => x.ID === v.skill_group);
      if (sg?.Skills?.length) filters.skill = sg.Skills.join(',');
    }

    if (v.agent_time_min != null) filters.agent_time_min = v.agent_time_min;
    if (v.agent_time_max != null) filters.agent_time_max = v.agent_time_max;
    if (v.acw_time_min          != null) filters.acw_time_min          = v.acw_time_min;
    if (v.acw_time_max          != null) filters.acw_time_max          = v.acw_time_max;
    if (v.active_talk_time_min  != null) filters.active_talk_time_min  = v.active_talk_time_min;
    if (v.active_talk_time_max  != null) filters.active_talk_time_max  = v.active_talk_time_max;
    if (v.summary)               filters.summary              = v.summary;
    if (v.topic)                 filters.topic                = v.topic;
    if (v.is_resolved)           filters.is_resolved          = v.is_resolved;
    if (v.audio_issues)          filters.audio_issues         = v.audio_issues;
    if (v.survey_mentioned)      filters.survey_mentioned     = v.survey_mentioned;
    if (v.call_hour?.length)     filters.call_hour            = v.call_hour.join(',');
    if (v.day_of_week?.length)   filters.day_of_week          = v.day_of_week.join(',');
    if (v.ani_called_back)       filters.ani_called_back      = v.ani_called_back;
    if (v.hold_time_min != null) filters.hold_time_min        = v.hold_time_min;
    if (v.hold_time_max != null) filters.hold_time_max        = v.hold_time_max;
    if (v.esc_or_transfer)          filters.esc_or_transfer          = v.esc_or_transfer;
    if (v.is_follow_up)             filters.is_follow_up             = v.is_follow_up;
    if (v.save_attempt_applicable)  filters.save_attempt_applicable  = v.save_attempt_applicable;
    if (v.save_attempt_made)        filters.save_attempt_made        = v.save_attempt_made;
    if (v.upselling_attempted)      filters.upselling_attempted      = v.upselling_attempted;
    if (v.upsell_has_policy)        filters.upsell_has_policy        = v.upsell_has_policy;
    if (v.ended_unexpectedly)       filters.ended_unexpectedly       = v.ended_unexpectedly;
    if (v.audio_issues_explanation) filters.audio_issues_explanation = v.audio_issues_explanation;
    if (v.city)                     filters.city                     = v.city;
    if (v.flagged)                  filters.flagged                  = v.flagged;
    if (v.stars?.length)            filters.stars                    = v.stars.join(',');
    if (v.called_support)           filters.called_support           = v.called_support;
    if (v.called_tier2)             filters.called_tier2             = v.called_tier2;

    _saveSession(sessionKey, v);
    onSearch(filters);
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({
      dateRange: defaultDateRange || (singleDate ? dayjs() : [dayjs().subtract(14, 'day'), dayjs()]),
      direction: ['Inbound'],
      contact_type: ['Original'],
      ...(defaultFilters || {}),
    });
  };

  // These nine columns hold descriptive values, not Yes/No: UpsellingAttempted
  // is 'upsell attempted' or 'informed technician can sell', AudioIssues is
  // 'minor' or 'silence', SaveAttemptMade is 'not applicable'. The list comes
  // from the data so the dropdown only offers values that can match.
  //
  // An empty list means the options request failed or returned nothing, and an
  // empty dropdown is the honest result -- falling back to Yes/No would put
  // the unmatchable values straight back.
  const aiFlagOptions = (col) =>
    (options.ai_flags?.[col] || []).map((v) => ({ label: v, value: v }));

  const selectProps = (items) => ({
    showSearch: true,
    allowClear: true,
    mode: 'multiple',
    optionFilterProp: 'label',
    options: items.map((v) => ({ label: v, value: v })),
  });

  return (
    <Card
      style={{ marginBottom: 16 }}
      title={<Space><FilterOutlined /><Typography.Text strong>Search Filters</Typography.Text></Space>}
      extra={(
        <Space>
          {panelFolded && (
            <Button size="small" type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
              {buttonLabel}
            </Button>
          )}
          <Button size="small" type="text" icon={panelFolded ? <DownOutlined /> : <UpOutlined />}
            onClick={() => foldPanel(!panelFolded)}>
            {panelFolded ? 'Show' : 'Hide'}
          </Button>
        </Space>
      )}
      styles={{ body: panelFolded ? { display: 'none' } : undefined }}
    >
      <Form form={form} layout="vertical">

          {/* ── Call Filters ── */}
          <SectionHeader label="Call Filters" collapsed={callCollapsed}
            onToggle={hideAIFilters ? undefined : () => setCallCollapsed((c) => !c)} />
          {!callCollapsed && (
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label={singleDate ? 'Date' : 'Date Range'} name="dateRange">
                  {singleDate
                    ? <DatePicker style={{ width: '100%' }} />
                    : <RangePicker style={{ width: '100%' }} />}
                </Form.Item>
              </Col>

              {!hide('direction') && <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Direction" name="direction">
                  <Select placeholder="All directions" {...selectProps(options.directions)} />
                </Form.Item>
              </Col>}

              {!hide('contact_type') && <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Contact Type" name="contact_type">
                  <Select placeholder="All types" {...selectProps(options.contact_types)} />
                </Form.Item>
              </Col>}

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Campaign" name="campaign">
                  <Select
                    placeholder={optionsLoading && !options.campaigns.length ? 'Loading…' : 'All campaigns'}
                    loading={optionsLoading && !options.campaigns.length}
                    showSearch
                    allowClear
                    {...(!singleCampaign && { mode: 'multiple' })}
                    optionFilterProp="label"
                    options={options.campaigns}
                  />
                </Form.Item>
              </Col>

              {skillGroups.length > 0 && <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Skill Group" name="skill_group">
                  <Select allowClear showSearch optionFilterProp="label" placeholder="No skill group"
                    options={groupOptions} disabled={!!pickedSkills?.length}
                    onChange={(id) => { if (id) form.setFieldValue('skill', []); }} />
                </Form.Item>
              </Col>}

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Skill" name="skill">
                  <Select placeholder={optionsLoading ? 'Loading…' : 'All skills'} loading={optionsLoading}
                    disabled={!!pickedGroup} {...selectProps(options.skills)} />
                </Form.Item>
              </Col>

              {!hide('media_name') && <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Media" name="media_name">
                  <Select placeholder="All media" {...selectProps(options.media_names)} />
                </Form.Item>
              </Col>}

              {!hide('contact_id') && <Col xs={24} sm={12} md={4} lg={3}>
                <Form.Item label="Contact ID" name="contact_id">
                  <Input placeholder="Exact ID" />
                </Form.Item>
              </Col>}

              {!hide('contact_id') && <Col xs={24} sm={12} md={4} lg={3}>
                <Form.Item label="Master Contact ID" name="master_contact_id">
                  <Input placeholder="Exact ID" />
                </Form.Item>
              </Col>}

              {!hide('agent_name') && <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Agent Name" name="agent_name">
                  <Select
                    placeholder="All agents"
                    showSearch allowClear mode="multiple" optionFilterProp="label"
                    options={options.agents}
                  />
                </Form.Item>
              </Col>}

              {!hide('disp_name') && <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Disposition" name="disp_name">
                  <Select placeholder="All dispositions" {...selectProps(options.dispositions)} />
                </Form.Item>
              </Col>}

              {!hide('contact_end_reason') && <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="End Reason" name="contact_end_reason">
                  <Select placeholder="All reasons" {...selectProps(options.contact_end_reasons)} />
                </Form.Item>
              </Col>}

              {!hide('ani_called_back') && <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="ANI Called Back Qty" name="ani_called_back">
                  <Select placeholder="Any" allowClear options={YES_NO} />
                </Form.Item>
              </Col>}

              {!hide('hold_time') && <Col xs={24} sm={12} md={8} lg={5}>
                <Form.Item label="Hold Time (seconds)">
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="hold_time_min" noStyle>
                      <InputNumber placeholder="Min" style={{ width: '50%' }} min={0} />
                    </Form.Item>
                    <Form.Item name="hold_time_max" noStyle>
                      <InputNumber placeholder="Max" style={{ width: '50%' }} min={0} />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
              </Col>}

              {!hide('call_hour') && <Col xs={24} sm={12} md={4} lg={3}>
                <Form.Item label="Call Hour (24h)" name="call_hour">
                  <Select
                    mode="multiple"
                    placeholder="Any hour"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={HOUR_OPTIONS}
                    maxTagCount="responsive"
                  />
                </Form.Item>
              </Col>}

              {!hide('day_of_week') && <Col xs={24} sm={12} md={4} lg={3}>
                <Form.Item label="Day of Week" name="day_of_week">
                  <Select
                    mode="multiple"
                    placeholder="Any day"
                    allowClear
                    optionFilterProp="label"
                    options={DAY_OPTIONS}
                    maxTagCount="responsive"
                  />
                </Form.Item>
              </Col>}

              {!hide('agent_time') && <Col xs={24} sm={12} md={8} lg={5}>
                <Form.Item label="Agent Time (seconds)">
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="agent_time_min" noStyle>
                      <InputNumber placeholder="Min" style={{ width: '50%' }} min={0} />
                    </Form.Item>
                    <Form.Item name="agent_time_max" noStyle>
                      <InputNumber placeholder="Max" style={{ width: '50%' }} min={0} />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
              </Col>}

              {!hide('acw_time') && <Col xs={24} sm={12} md={8} lg={5}>
                <Form.Item label="ACW Time (seconds)">
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="acw_time_min" noStyle>
                      <InputNumber placeholder="Min" style={{ width: '50%' }} min={0} />
                    </Form.Item>
                    <Form.Item name="acw_time_max" noStyle>
                      <InputNumber placeholder="Max" style={{ width: '50%' }} min={0} />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
              </Col>}

              {!hide('active_talk_time') && <Col xs={24} sm={12} md={8} lg={5}>
                <Form.Item label="Active Talk Time (seconds)">
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="active_talk_time_min" noStyle>
                      <InputNumber placeholder="Min" style={{ width: '50%' }} min={0} />
                    </Form.Item>
                    <Form.Item name="active_talk_time_max" noStyle>
                      <InputNumber placeholder="Max" style={{ width: '50%' }} min={0} />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
              </Col>}

              {!hide('flagged') && <Col xs={24} sm={12} md={5} lg={4}>
                <Form.Item label="Flagged" name="flagged">
                  <Select placeholder="All calls" allowClear options={FLAGGED_OPTIONS} />
                </Form.Item>
              </Col>}

              {showCalledFilters && <Col xs={24} sm={12} md={5} lg={4}>
                <Form.Item label="Called Support" name="called_support">
                  <Select placeholder="Either" allowClear options={YES_NO} />
                </Form.Item>
              </Col>}

              {showCalledFilters && <Col xs={24} sm={12} md={5} lg={4}>
                <Form.Item label="Called Tier 2" name="called_tier2">
                  <Select placeholder="Either" allowClear options={YES_NO} />
                </Form.Item>
              </Col>}
            </Row>
          )}

          {!hideAIFilters && <Divider style={{ margin: '4px 0 12px' }} />}

          {/* ── AI Filters ── */}
          {!hideAIFilters && (<>
            <SectionHeader
              label="AI Filters"
              collapsed={aiCollapsed}
              onToggle={() => setAiCollapsed((c) => !c)}
            />
            {!aiCollapsed && (
            <Row gutter={16}>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Summary Contains" name="summary">
                  <Input placeholder="Enter keyword" />
                </Form.Item>
              </Col>

              {hasTab('reasons') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Reason" name="reason">
                    <Select placeholder="All reasons" {...selectProps(options.reasons)} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('reasons') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Outcome" name="outcome">
                    <Select placeholder="All outcomes" {...selectProps(options.outcomes)} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('topics', 'topic_cat') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Topic Category" name="topic_category">
                    <Select placeholder="All categories" {...selectProps(options.topic_categories)} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('topics', 'topic_cat') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Topic" name="topic">
                    <Input placeholder="Contains..." allowClear />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Resolved" name="is_resolved">
                  <Select placeholder="Any" allowClear options={IS_RESOLVED} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Stars" name="stars">
                  <Select placeholder="Any" {...selectProps(options.stars)} />
                </Form.Item>
              </Col>

              {hasTab('audio') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Audio Issues" name="audio_issues">
                    <Select placeholder="Any" allowClear options={aiFlagOptions('AudioIssues')} />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Survey Mentioned" name="survey_mentioned">
                  <Select placeholder="Any" allowClear options={aiFlagOptions('SurveyMentioned')} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Sentiment Start" name="sentiment_start">
                  <Select placeholder="Any" allowClear showSearch optionFilterProp="label"
                    options={(options.sentiment_starts || []).map((v) => ({ label: v, value: v }))} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Sentiment End" name="sentiment_end">
                  <Select placeholder="Any" allowClear showSearch optionFilterProp="label"
                    options={(options.sentiment_ends || []).map((v) => ({ label: v, value: v }))} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Escalated / Transferred" name="esc_or_transfer">
                  <Select placeholder="Any" allowClear options={aiFlagOptions('EscOrTransfer')} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Is Follow-Up" name="is_follow_up">
                  <Select placeholder="Any" allowClear options={aiFlagOptions('IsFollowUp')} />
                </Form.Item>
              </Col>

              {hasTab('save') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Save Attempt Applicable" name="save_attempt_applicable">
                    <Select placeholder="Any" allowClear options={aiFlagOptions('SaveAttemptApplicable')} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('save') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Save Attempt Made" name="save_attempt_made">
                    <Select placeholder="Any" allowClear options={aiFlagOptions('SaveAttemptMade')} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('save') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Save Attempt Outcome" name="save_attempt_outcome">
                    <Select placeholder="Any" allowClear showSearch optionFilterProp="label"
                      options={(options.save_attempt_outcomes || []).map((v) => ({ label: v, value: v }))} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('upsell') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Upsell Attempted" name="upselling_attempted">
                    <Select placeholder="Any" allowClear options={aiFlagOptions('UpsellingAttempted')} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('upsell') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Upsell Circumstance" name="upsell_circumstance">
                    <Select placeholder="Any" allowClear showSearch optionFilterProp="label"
                      options={(options.upsell_circumstances || []).map((v) => ({ label: v, value: v }))} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('upsell') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Upsell Has Policy" name="upsell_has_policy">
                    <Select placeholder="Any" allowClear options={aiFlagOptions('UpsellHasPolicy')} />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Ended Unexpectedly" name="ended_unexpectedly">
                  <Select placeholder="Any" allowClear options={aiFlagOptions('EndedUnexpectedly')} />
                </Form.Item>
              </Col>

              {hasTab('audio') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Audio Issues Note" name="audio_issues_explanation">
                    <Input placeholder="Keyword in explanation" />
                  </Form.Item>
                </Col>
              )}

              {hasTab('oppt') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Opportunity Type" name="oppt_type">
                    <Select placeholder="All types" {...selectProps(options.oppt_types)} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('oppt') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Opportunity" name="oppt_opportunity">
                    <Select placeholder="All opportunities" {...selectProps(options.oppt_opportunities)} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('oppt') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Opportunity Outcome" name="oppt_outcome">
                    <Select placeholder="All outcomes" {...selectProps(options.oppt_outcomes)} />
                  </Form.Item>
                </Col>
              )}

              {hasTab('oppt') && (
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Form.Item label="Opportunity Reason" name="oppt_reason">
                    <Select placeholder="All reasons" {...selectProps(options.oppt_reasons)} />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="State" name="state">
                  <Select placeholder="All states" {...selectProps(options.states)} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="City" name="city">
                  <Input placeholder="Contains..." allowClear />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="ZIP" name="zip">
                  <Select placeholder="All ZIPs" {...selectProps(options.zips)} />
                </Form.Item>
              </Col>
            </Row>
            )}
          </>)}

          <Row>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                >
                  {buttonLabel}
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>
                  Reset
                </Button>
                {/* A page's own note beside the buttons (searchNote). */}
                {searchNote && <Typography.Text type="secondary">{searchNote}</Typography.Text>}
              </Space>
            </Col>
          </Row>
        </Form>
    </Card>
  );
});

export default FilterPanel;
